import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaMagic, FaMicrophone, FaSignOutAlt, FaUpload, FaArrowRight, FaBriefcase } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [isTargeted, setIsTargeted] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [quota, setQuota] = useState(null);

  const maxPromptLength = 5000;
  const maxJobLength = 5000;

  useEffect(() => {
    const pendingPrompt = localStorage.getItem('pendingPrompt');
    if (pendingPrompt) {
      try {
        const data = JSON.parse(pendingPrompt);
        setPrompt(data.prompt || '');
        if (data.jobDescription) {
          setIsTargeted(true);
          setJobDescription(data.jobDescription);
        }
        localStorage.removeItem('pendingPrompt');
      } catch (e) {
        console.error('Failed to parse pending prompt:', e);
      }
    }
    fetchQuota();
  }, []);

  const fetchQuota = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/quota`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setQuota(data);
      }
    } catch (error) {
      console.error('Failed to fetch quota:', error);
    }
  };

  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('La reconnaissance vocale n\'est pas supportée.');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setPrompt(prev => (prev + ' ' + transcript).trim().slice(0, maxPromptLength));
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.start();
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      alert('Veuillez décrire votre parcours');
      return;
    }
    navigate('/preview', { state: { prompt: prompt.trim(), jobDescription: isTargeted ? jobDescription.trim() : null } });
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
      logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        setPrompt(text?.toString().slice(0, maxPromptLength) || '');
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1d] text-gray-200">
      {/* Header */}
      <nav className="border-b border-gray-800 bg-[#242428]/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaMagic className="w-5 h-5 sm:w-6 sm:h-6 text-[#00d9ff]" />
            <span className="text-lg sm:text-xl font-bold">
              SkillForge <span className="text-[#00d9ff]">Pro</span>
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {quota && (
              <div className="text-xs sm:text-sm text-gray-400">
                <span className="font-medium text-[#00d9ff]">{quota.remaining}</span> / {quota.limit} CV
              </div>
            )}
            <div className="hidden sm:flex items-center gap-2">
              {user?.picture && (<img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />)}
              <span className="text-sm">{user?.name || user?.email}</span>
            </div>
            <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-gray-800 transition-colors" title="Déconnexion">
              <FaSignOutAlt className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-12 sm:pt-20 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2">Créez votre CV avec l'IA</h1>
          <p className="text-center text-gray-400 text-sm sm:text-base mb-6 sm:mb-8">Décrivez votre parcours, l'IA génère un CV professionnel</p>

          {/* Job Description Field (Conditional) */}
          {isTargeted && (
            <div className="mb-4 animate-fade-in">
              <div className="relative">
                <textarea 
                  value={jobDescription} 
                  onChange={(e) => setJobDescription(e.target.value.slice(0, maxJobLength))} 
                  placeholder="Collez l'annonce du poste ciblé..."
                  className="w-full p-4 rounded-2xl border border-gray-700 bg-[#242428] text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00d9ff] resize-none text-sm sm:text-base"
                  rows={3}
                />
                <div className="absolute top-2 right-3 text-xs text-gray-500">{jobDescription.length}/{maxJobLength}</div>
              </div>
            </div>
          )}

          {/* Main Prompt */}
          <div className="relative">
            <textarea 
              value={prompt} 
              onChange={(e) => setPrompt(e.target.value.slice(0, maxPromptLength))} 
              placeholder="Décrivez votre parcours, vos compétences et votre expérience..." 
              className="w-full p-4 sm:p-6 pr-32 sm:pr-36 rounded-2xl border border-gray-700 bg-[#242428] text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00d9ff] resize-none text-sm sm:text-base" 
              rows={window.innerWidth < 640 ? 6 : 8}
            />
            
            {/* Icon Bar */}
            <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4 flex items-center justify-between">
              {/* Left Icons */}
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => setIsTargeted(!isTargeted)}
                  className={`group flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-all ${
                    isTargeted ? 'bg-[#00d9ff] text-black' : 'hover:bg-gray-700 text-gray-400'
                  }`}
                  title="Poste ciblé"
                >
                  <FaBriefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline text-xs font-medium">Poste ciblé</span>
                </button>
              </div>

              {/* Right Icons */}
              <div className="flex items-center gap-1 sm:gap-2">
                <label className="p-1.5 sm:p-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-700 text-gray-400" title="Uploader un CV">
                  <FaUpload className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <input type="file" accept=".txt" onChange={handleFileUpload} className="hidden" />
                </label>

                <button onClick={startVoiceInput} disabled={isListening} className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                  isListening ? 'bg-red-500 text-white animate-pulse' : 'hover:bg-gray-700 text-gray-400'
                }`} title="Dicter">
                  <FaMicrophone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>

                <button onClick={handleGenerate} disabled={!prompt.trim()} className={`p-2 sm:p-2.5 rounded-lg transition-all ${
                  prompt.trim() ? 'bg-[#00d9ff] hover:bg-[#00c4ea] text-black' : 'bg-gray-700 text-gray-600'
                }`} title="Générer">
                  <FaArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>

            <div className="absolute top-2 right-3 text-xs text-gray-500">{prompt.length}/{maxPromptLength}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
